import { store } from './store.js';
/* 
 * workBoard.js
 * Manages the "Mesa de Trabajo" (Manipulatives Board)
 * Supports Mouse and Touch events for moving counters.
 */

const WorkBoard = {
    modalId: 'workBoardModal',
    workAreaId: 'workArea',
    trashId: 'trashZone',
    toolbarId: 'workToolbar',
    zIndexCounter: 100,

    // Modes
    removeMode: false,
    eraseMode: false,
    countingMode: false,

    // Active Counting Regions (Set of region IDs)
    activeRegions: new Set(),

    // Mapeo extendido de emoji para magnitudes comunes
    EMOJI_MAP: {
        'manzana': '🍎', 'pera': '🍐', 'platano': '🍌', 'naranja': '🍊', 'fresa': '🍓',
        'uva': '🍇', 'sandia': '🍉', 'melon': '🍈', 'piña': '🍍', 'limon': '🍋',
        'tomate': '🍅', 'zanahoria': '🥕', 'lechuga': '🥬', 'patata': '🥔',
        'huevo': '🥚', 'leche': '🥛', 'galleta': '🍪', 'caramelo': '🍬',
        'pelota': '⚽', 'coche': '🚗', 'muñeca': '🪆', 'peluche': '🧸',
        'libro': '📚', 'lapiz': '✏️', 'cuaderno': '📓', 'mochila': '🎒',
        'pajaro': '🐦', 'perro': '🐶', 'gato': '🐱', 'hamster': '🐹',
        'pez': '🐟', 'tortuga': '🐢', 'vaca': '🐄', 'oveja': '🐑',
        'cerdo': '🐷', 'gallina': '🐔', 'pollito': '🐥', 'caballo': '🐎',
        'moneda': '🪙', 'billete': '💵', 'ticket': '🎟️', 'premio': '🏆',
        'regalo': '🎁', 'globo': '🎈', 'vela': '🕯️', 'tarta': '🍰',
        'estrella': '⭐', 'flor': '🌸', 'hoja': '🍃', 'piedra': '🪨',
        'niño': '👦', 'niña': '👧', 'amigo': '🧒', 'persona': '👥'
    },

    init() {
        // Init logic
    },

    async open() {
        if (!document.getElementById(this.modalId)) {
            await this.createDOM();
        } else {
            // Si ya existe, refrescar los textos por si ha cambiado el problema
            await this.refreshTexts();
        }

        const modal = document.getElementById(this.modalId);
        if (modal) {
            modal.style.display = 'flex';
            modal.classList.remove('hidden');
            document.body.style.overflow = 'hidden';
            this.playPop();
            this.refreshToolbar(); // Actualizar iconos según el problema actual
        }
    },

    async getProblemTextElements() {
        if (!store.state || !store.state.problems) return { start: '', change: '', question: '' };

        // PRIORIDAD: Obtener los mismos textos que el modal "Un Poco de Ayuda"
        if (window.getUnifiedProblemHelp) {
            try {
                const helpData = await window.getUnifiedProblemHelp();
                if (helpData && helpData.ayuda) {
                    return {
                        start: helpData.ayuda.inicio.texto || '',
                        change: helpData.ayuda.cambio.texto || '',
                        question: helpData.ayuda.pregunta.texto || ''
                    };
                }
            } catch (e) {
                console.warn('Error fetching unified help for WorkBoard:', e);
            }
        }

        const problem = store.state.problems[store.state.currentProblem];
        if (!problem) return { start: '', change: '', question: '' };

        let statement = '';
        if (typeof problem.statement === 'string') {
            statement = problem.statement;
        } else if (problem.statement) {
            statement = problem?.statement?.['es-ES'] || Object.values(problem.statement)[0] || '';
        }

        // Obtener datos numéricos para mejorar el parseo (Fallback)
        const usefulData = problem.data && problem.data.useful ? problem.data.useful : [];
        const data1 = usefulData[0] !== undefined ? usefulData[0] : (problem.data1 || problem.num1);
        const data2 = usefulData[1] !== undefined ? usefulData[1] : (problem.data2 || problem.num2);

        if (window.parseProblemStatement) {
            return window.parseProblemStatement(statement, data1, data2);
        }

        const questionMatch = statement.match(/[¿]([^?]+)[?]/);
        const question = questionMatch ? questionMatch[0] : '';
        const textWithoutQuestion = statement.replace(/[¿][^?]+[?]/, '').trim();
        const parts = textWithoutQuestion.split(/\s+(?:y|pero|,\s*pero|entonces)\s+/);

        return {
            start: parts[0] || '...',
            change: parts[1] || '...',
            question: question || '...'
        };
    },

    async refreshTexts() {
        const texts = await this.getProblemTextElements();
        const regionStart = document.querySelector('#region-start div[style*="Baloo 2"]');
        const regionChange = document.querySelector('#region-change div[style*="Baloo 2"]');
        const regionQuestion = document.querySelector('#region-question div[style*="Baloo 2"]');

        if (regionStart) regionStart.innerHTML = texts.start;
        if (regionChange) regionChange.innerHTML = texts.change;
        if (regionQuestion) regionQuestion.innerHTML = texts.question;

        // Actualizar también los botones de audio si existen
        const area = document.getElementById(this.workAreaId);
        if (area) {
            const btnStart = area.querySelector('#region-start .btn-audio-inline');
            const btnChange = area.querySelector('#region-change .btn-audio-inline');
            const btnQuestion = area.querySelector('#region-question .btn-audio-inline');

            if (btnStart) btnStart.onclick = (e) => { e.stopPropagation(); window.toggleAudio(btnStart, texts.start); };
            if (btnChange) btnChange.onclick = (e) => { e.stopPropagation(); window.toggleAudio(btnChange, texts.change); };
            if (btnQuestion) btnQuestion.onclick = (e) => { e.stopPropagation(); window.toggleAudio(btnQuestion, texts.question); };
        }
    },

    async createDOM() {
        const div = document.createElement('div');
        div.id = this.modalId;
        div.className = 'hidden';
        div.style.cssText = "position:fixed; top:0; left:0; width:100%; height:100%; background:rgba(0,0,0,0.85); z-index:9000; align-items:center; justify-content:center; display:none; backdrop-filter:blur(4px);";

        // Get dynamic text
        const texts = await this.getProblemTextElements();

        div.innerHTML = `
            <div class="work-board-container">
                
                <!-- Header -->
                <div class="work-header">
                    <h2 class="baloo-font">🟫🔍 Mesa de Pruebas</h2>

                    <div class="work-header-actions">
                        <button id="btnCount" class="btn" onclick="window.WorkBoard.toggleCountMode()" 
                            style="background:#3B82F6; color:#FFF; border-radius:12px; font-weight:bold; border:none; cursor:pointer; box-shadow: 0 4px 0 #2563EB; display:flex; align-items:center; gap:5px;">
                            <span>🔢</span> <span class="btn-text">CONTAR</span>
                        </button>
                        
                        <button id="btnRemoveInit" class="btn" onclick="window.WorkBoard.toggleRemoveMode()" 
                            style="background:#F59E0B; color:#000; border-radius:12px; font-weight:bold; border:none; cursor:pointer; box-shadow: 0 4px 0 #D97706; display:flex; align-items:center; gap:5px;">
                            <span>❌</span> <span class="btn-text">QUITAR</span>
                        </button>
 
                        <button id="btnEraseInit" class="btn" onclick="window.WorkBoard.toggleEraseMode()" 
                            style="background:#8b5cf6; color:#FFF; border-radius:12px; font-weight:bold; border:none; cursor:pointer; box-shadow: 0 4px 0 #7c3aed; display:flex; align-items:center; gap:5px;">
                            <span>🧽</span> <span class="btn-text">BORRAR</span>
                        </button>
 
                        <button id="btnClear" class="btn" onclick="window.WorkBoard.clearTable()" 
                            style="background:#EF4444; color:#FFF; border-radius:12px; font-weight:bold; border:none; cursor:pointer; box-shadow: 0 4px 0 #B91C1C; display:flex; align-items:center; gap:5px;">
                            <span>🗑️</span> <span class="btn-text">LIMPIAR</span>
                        </button>
                        
                        <button class="btn btn-secondary" onclick="window.WorkBoard.close()" 
                            style="background:#000; color:#FFF; border-radius:15px; font-weight:900; border:none; cursor:pointer;">
                            CERRAR
                        </button>
                    </div>
                </div>

                <!-- Area de Trabajo -->
                <div id="${this.workAreaId}" class="work-area">
                    
                    <!-- BACKGROUND LAYOUT -->
                    <div class="work-regions-container">
                        
                        <!-- MITAD SUPERIOR (Dos cuadrantes en desktop, dos filas en móvil) -->
                        <div class="work-row work-row-top">
                            <!-- Cuadrante Izquierdo -->
                            <div id="region-start" class="work-region work-region-start">
                                <div style="display:flex; align-items:center; gap:8px; margin-bottom:5px;">
                                    <div style="background:#FEF3C7; color:#92400E; padding:5px 10px; border-radius:8px; font-weight:bold; font-size: 0.8rem;">
                                        AL PRINCIPIO...
                                    </div>
                                    <button class="btn-audio-inline" onclick="window.toggleAudio(this, '${texts.start.replace(/'/g, "\\'")}')" style="pointer-events:auto; z-index:10;"><span>🔊</span></button>
                                </div>
                                <div class="region-text" style="font-family:'Baloo 2', cursive; font-size:1.1rem; color:#000; font-weight:bold; overflow-y: auto; pointer-events:none;">
                                    ${texts.start}
                                </div>
                                <div class="count-result-display" style="position:absolute; top:40px; right:15px; pointer-events:none; z-index:100; display: flex; gap: 5px;"></div>
                            </div>

                            <!-- Cuadrante Derecho -->
                            <div id="region-change" class="work-region work-region-change">
                                <div style="display:flex; align-items:center; gap:8px; margin-bottom:5px;">
                                    <div style="background:#DBEAFE; color:#1E40AF; padding:5px 10px; border-radius:8px; font-weight:bold; font-size: 0.8rem;">
                                        DESPUÉS...
                                    </div>
                                    <button class="btn-audio-inline" onclick="window.toggleAudio(this, '${texts.change.replace(/'/g, "\\'")}')" style="pointer-events:auto; z-index:10;"><span>🔊</span></button>
                                </div>
                                <div class="region-text" style="font-family:'Baloo 2', cursive; font-size:1.1rem; color:#000; font-weight:bold; overflow-y: auto; pointer-events:none;">
                                    ${texts.change}
                                </div>
                                <div class="count-result-display" style="position:absolute; top:40px; right:15px; pointer-events:none; z-index:100; display: flex; gap: 5px;"></div>
                            </div>
                        </div>

                        <!-- MITAD INFERIOR (Pregunta) -->
                        <div id="region-question" class="work-row work-row-bottom work-region">
                            <div>
                                <div style="display:flex; align-items:center; gap:8px; margin-bottom:5px;">
                                    <div style="background:#FCE7F3; color:#9D174D; padding:5px 10px; border-radius:8px; font-weight:bold; font-size: 0.8rem;">
                                        PREGUNTA
                                    </div>
                                    <button class="btn-audio-inline" onclick="window.toggleAudio(this, '${texts.question.replace(/'/g, "\\'")}')" style="pointer-events:auto; z-index:10;"><span>🔊</span></button>
                                </div>
                                <div class="region-text" style="font-family:'Baloo 2', cursive; font-size:1.2rem; color:#000; font-weight:bold; pointer-events:none;">
                                    ${texts.question}
                                </div>
                            </div>
                            <div class="count-result-display" style="position:absolute; top:40px; right:15px; pointer-events:none; z-index:100; display: flex; gap: 5px;"></div>
                        </div>

                    </div>
                    <!-- END BACKGROUND LAYOUT -->

                    <div id="${this.trashId}" class="work-trash"
                        style="position:absolute; bottom:15px; right:15px; width:65px; height:65px; border:3px dashed #FF4444; border-radius:15px; display:flex; align-items:center; justify-content:center; background:rgba(255, 229, 229, 0.9); font-size:2rem; transition:transform 0.2s; z-index:50;">
                        🗑️
                    </div>
                </div>

                <!-- Toolbar Dinámica -->
                <div id="${this.toolbarId}" class="work-toolbar">
                    <!-- Dinámico -->
                </div>
            </div>
        `;

        document.body.appendChild(div);

        // Add click listener for Counting Mode
        const areaDiv = document.getElementById(this.workAreaId);
        if (areaDiv) {
            areaDiv.addEventListener('pointerdown', (e) => {
                if (this.countingMode) {
                    this.handleRegionCount(e);
                }
            });
        }

        this.setupTouchSupport();
    },

    refreshToolbar() {
        const toolbar = document.getElementById(this.toolbarId);
        if (!toolbar) return;

        // Limpiar toolbar
        toolbar.innerHTML = '';

        // 1. Añadir Barras de Decenas y Unidades Genéricas (Base ABN)
        const generics = [
            { type: 'ten-bar', icon: '📏', color: '#EF4444', label: 'Decena' },
            { type: 'unit-cube', icon: '🟦', color: '#3B82F6', label: 'Unidad' }
        ];

        generics.forEach(item => {
            toolbar.appendChild(this.createToolbarItem(item.type, item.icon, item.label, item.color));
        });

        // Hint Text with audio button
        const hintText = 'Arrastra las unidades y las decenas que indican los datos del problema "AL PRINCIPIO..." y "DESPUÉS..." y por último, coloca las unidades y decenas que responden a la solución del problema.';
        const hintDiv = document.createElement('div');
        hintDiv.className = 'work-toolbar-hint';
        hintDiv.style.cssText = 'flex: 1; font-family: "Baloo 2", cursive; color: #475569; font-weight: 500; font-size: 1.1rem; padding: 0 20px; text-align: center; border-left: 2px solid #E2E8F0; margin-left: 20px; display: flex; align-items: center; justify-content: center; gap: 12px; height: 100%;';
        hintDiv.innerHTML = `
            <span>${hintText}</span>
            <button class="btn-audio-inline" id="workBoardHintAudioBtn"
                style="flex-shrink:0; pointer-events:auto;"
                onclick="if(window.toggleAudio) window.toggleAudio(this, '${hintText.replace(/'/g, "\\'")}')"
            ><span>🔊</span></button>
        `;
        toolbar.appendChild(hintDiv);
    },

    createToolbarItem(type, icon, label, color) {
        const item = document.createElement('div');
        item.className = 'counter-source';
        item.draggable = true;
        item.dataset.type = type;
        item.dataset.icon = icon;
        item.dataset.color = color;
        // Styles are now mostly in CSS class, we keep cursor and transition if needed for base states
        item.style.cssText = `cursor:grab; display:flex; flex-direction:column; align-items:center; gap:5px; border-radius:15px; transition:background 0.2s;`;

        // Efecto hover
        item.onmouseover = () => item.style.background = '#F1F5F9';
        item.onmouseout = () => item.style.background = 'transparent';

        item.innerHTML = `
            <div style="font-size: 2.8rem; height:50px; display:flex; align-items:center; justify-content:center;">${icon}</div>
            <span style="font-size:0.75rem; font-weight:800; color:#475569; text-transform:uppercase; font-family:'Baloo 2', cursive;">${label}</span>
        `;

        const spawnHandler = (e) => {
            e.preventDefault();
            const container = document.getElementById(this.workAreaId);
            const rect = container.getBoundingClientRect();
            this.spawnItem(type, icon, color, rect.width / 2 + (Math.random() * 60 - 30), rect.height / 2 + (Math.random() * 60 - 30));
        };

        item.ondragstart = (e) => this.handleDragStart(e);
        item.onclick = spawnHandler;
        item.addEventListener('touchstart', spawnHandler, { passive: false });

        return item;
    },

    close() {
        const modal = document.getElementById(this.modalId);
        if (modal) {
            modal.style.display = 'none';
            modal.classList.add('hidden');
            document.body.style.overflow = '';
        }
    },

    handleDragStart(e) {
        if (this.countingMode) {
            e.preventDefault();
            return;
        }
        const ds = e.currentTarget.dataset;
        e.dataTransfer.setData('type', ds.type);
        e.dataTransfer.setData('icon', ds.icon);
        e.dataTransfer.setData('color', ds.color);
        e.dataTransfer.effectAllowed = 'copy';
    },

    spawnItem(type, icon, color, x, y) {
        const container = document.getElementById(this.workAreaId);
        const el = document.createElement('div');
        el.className = 'work-item';
        el.style.position = 'absolute';
        el.style.left = x + 'px';
        el.style.top = y + 'px';
        el.style.cursor = 'grab';
        el.style.zIndex = this.zIndexCounter++;
        el.style.touchAction = 'none';

        if (type === 'ten-bar') {
            el.dataset.type = 'ten-bar';
            el.style.width = '18px';
            el.style.height = '110px';
            el.style.background = '#EF4444';
            el.style.borderRadius = '8px';
            el.style.border = '3px solid #991B1B';
            el.style.display = 'flex';
            el.style.flexDirection = 'column';
            el.style.justifyContent = 'space-around';
            el.style.alignItems = 'center';
            el.style.padding = '5px 0';
            for (let i = 0; i < 10; i++) {
                const dot = document.createElement('div');
                dot.style.width = '12px';
                dot.style.height = '8px';
                dot.style.background = 'rgba(255,255,255,0.3)';
                dot.style.borderRadius = '2px';
                el.appendChild(dot);
            }
        } else if (type === 'unit-cube') {
            el.dataset.type = 'unit-cube';
            el.style.width = '32px';
            el.style.height = '32px';
            el.style.background = '#3B82F6';
            el.style.border = '3px solid #1E40AF';
            el.style.borderRadius = '6px';
        } else {
            el.dataset.type = 'magnitude';
            el.style.width = '65px';
            el.style.height = '65px';
            el.innerHTML = `<div style="font-size: 3.5rem; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; pointer-events: none; user-select: none;">${icon}</div>`;
        }

        container.appendChild(el);

        this.snapToRegion(el); // This will trigger updateAllActiveCounts via snap logic or we call it here
        this.updateAllActiveCounts(); // Ensure count updates when adding

        // Animación
        el.animate([
            { transform: 'scale(0) rotate(-20deg)', opacity: 0 },
            { transform: 'scale(1.1) rotate(5deg)', opacity: 1 },
            { transform: 'scale(1) rotate(0deg)', opacity: 1 }
        ], { duration: 300, easing: 'ease-out' });

        this.playPop();
    },


    makeDraggable(el) {
        let isDragging = false;
        let startX, startY, initialLeft, initialTop;

        const onPointerDown = (e) => {
            // CHECK COUNT MODE FIRST
            if (this.countingMode) {
                e.preventDefault();
                e.stopPropagation();
                this.handleRegionCount(e);
                return;
            }

            // Check Erase Mode
            if (this.eraseMode) {
                e.stopPropagation();
                e.preventDefault();
                el.remove();
                this.playPop();
                this.updateAllActiveCounts(); // Update counts
                return;
            }

            // Check Remove Mode (Mark X)
            if (this.removeMode) {
                e.stopPropagation();
                e.preventDefault();

                el.classList.toggle('marked-x');
                if (el.classList.contains('marked-x')) {
                    let xOverlay = el.querySelector('.x-mark-overlay');
                    if (!xOverlay) {
                        xOverlay = document.createElement('div');
                        xOverlay.className = 'x-mark-overlay';
                        xOverlay.style.cssText = 'position:absolute; top:0; left:0; width:100%; height:100%; display:flex; align-items:center; justify-content:center; pointer-events:none; z-index:10;';
                        xOverlay.innerHTML = '<span style="color:red; font-size:4rem; font-weight:bold; text-shadow:2px 2px 0px white;">X</span>';
                        el.appendChild(xOverlay);
                        el.style.opacity = '0.6';
                    }
                    this.playPop();
                } else {
                    const xOverlay = el.querySelector('.x-mark-overlay');
                    if (xOverlay) xOverlay.remove();
                    el.style.opacity = '1';
                }
                this.updateAllActiveCounts(); // Update counts (as X marked items are excluded)
                return;
            }

            isDragging = true;
            el.style.zIndex = ++this.zIndexCounter;
            el.style.cursor = 'grabbing';
            el.style.filter = 'brightness(1.1) shadow(0 5px 15px rgba(0,0,0,0.3))';

            const rect = el.getBoundingClientRect();
            startX = e.clientX;
            startY = e.clientY;
            initialLeft = el.offsetLeft;
            initialTop = el.offsetTop;

            el.setPointerCapture(e.pointerId);
            e.stopPropagation();
        };

        const onPointerMove = (e) => {
            if (!isDragging) return;
            const dx = e.clientX - startX;
            const dy = e.clientY - startY;
            el.style.left = (initialLeft + dx) + 'px';
            el.style.top = (initialTop + dy) + 'px';
        };

        const onPointerUp = (e) => {
            if (!isDragging) return;
            isDragging = false;
            el.style.cursor = 'grab';
            el.style.filter = '';
            el.releasePointerCapture(e.pointerId);

            const trashed = this.checkTrash(el, e.clientX, e.clientY);
            if (!trashed) {
                this.snapToRegion(el);
            }
            this.updateAllActiveCounts(); // Update counts after move/drop
        };

        el.addEventListener('pointerdown', onPointerDown);
        el.addEventListener('pointermove', onPointerMove);
        el.addEventListener('pointerup', onPointerUp);
        el.addEventListener('pointercancel', onPointerUp); // Added for mobile swipe interruptions
        
        el.addEventListener('touchmove', (e) => {
            if (isDragging) e.preventDefault();
        });
    },

    handleRegionCount(e) {
        if (!this.countingMode) return;

        const zone = document.getElementById(this.workAreaId);
        const rect = zone.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;
        const W = rect.width;
        const H = rect.height;
        const midX = W / 2;
        const midY = H / 2;

        let regionId = "";

        // Identificar zona clickeada
        if (clickY >= midY) {
            regionId = "region-question";
        } else {
            if (clickX < midX) {
                regionId = "region-start";
            } else {
                regionId = "region-change";
            }
        }

        // TOGGLE LOGIC
        if (this.activeRegions.has(regionId)) {
            // If already active, deactivate it
            this.activeRegions.delete(regionId);
            const container = document.getElementById(regionId);
            if (container) {
                const display = container.querySelector('.count-result-display');
                if (display) display.innerHTML = '';
            }
            if (typeof showToast === 'function') showToast("Contador oculto");
        } else {
            // Activate it
            this.activeRegions.add(regionId);
            this.updateRegionCount(regionId);
        }

        this.toggleCountMode();
    },

    // New: Update all active counts
    updateAllActiveCounts() {
        this.activeRegions.forEach(regionId => {
            this.updateRegionCount(regionId);
        });
    },

    // New: Calculate and display count for a specific region
    updateRegionCount(regionId) {
        const zone = document.getElementById(this.workAreaId);
        if (!zone) return;

        const W = zone.offsetWidth;
        const H = zone.offsetHeight;
        const midX = W / 2;
        const midY = H / 2;

        // Define filter for this region
        let filterFn = null;
        if (regionId === "region-question") {
            filterFn = (cy, cx) => cy >= midY;
        } else if (regionId === "region-start") {
            filterFn = (cy, cx) => cy < midY && cx < midX;
        } else if (regionId === "region-change") {
            filterFn = (cy, cx) => cy < midY && cx >= midX;
        }

        if (!filterFn) return;

        // Count items
        const items = zone.querySelectorAll('.work-item');
        let count = 0;

        items.forEach(item => {
            if (item.classList.contains('marked-x')) return;

            const ix = item.offsetLeft + item.offsetWidth / 2;
            const iy = item.offsetTop + item.offsetHeight / 2;

            if (filterFn(iy, ix)) {
                if (item.dataset.type === 'ten-bar') count += 10;
                else count += 1;
            }
        });

        // Display
        const container = document.getElementById(regionId);
        if (container) {
            const display = container.querySelector('.count-result-display');
            if (display) {
                const tens = Math.floor(count / 10);
                const units = count % 10;

                let html = '';
                if (tens > 0 || count === 0 && this.activeRegions.has(regionId)) {
                    // Show 0 if active, or if > 0
                    if (tens > 0) html += `<span style="color:#EF4444; font-weight:900; font-size:4.5rem; text-shadow: 2px 2px 0px white;">${tens}</span>`;
                }
                html += `<span style="color:#3B82F6; font-weight:900; font-size:4.5rem; text-shadow: 2px 2px 0px white;">${units}</span>`;

                // Only animate if content changed significantly? 
                // Using innerHTML replacement causes re-render.
                if (display.innerHTML !== html) {
                    display.innerHTML = html;
                }
            }
        }
    },

    showFeedback(message, type = 'info') {
        // Remove existing feedback if any
        const existing = document.getElementById('workboard-feedback-modal');
        if (existing) existing.remove();

        // Determine Detective Character
        const char = (window.UserManager && typeof window.UserManager.loadData === 'function')
            ? (window.UserManager.loadData('mentorCharacter') || 'boy')
            : 'boy';

        // Image path assumption
        const imgPath = `images/detectives/${char}/detective_${char}_neutral.png`;

        const modal = document.createElement('div');
        modal.id = 'workboard-feedback-modal';
        modal.style.cssText = `
            position: absolute; top: 0; left: 0; width: 100%; height: 100%;
            display: flex; align-items: center; justify-content: center;
            z-index: 10000; pointer-events: auto;
            background: rgba(0,0,0,0.4); backdrop-filter: blur(2px);
            animation: fadeIn 0.3s ease-out;
        `;

        // Define colors
        const color = type === 'success' ? '#10B981' : '#3B82F6';

        modal.innerHTML = `
            <div style="
                background: white; width: 500px; max-width: 90%; border-radius: 20px;
                box-shadow: 0 20px 50px rgba(0,0,0,0.3); overflow: visible;
                display: flex; flex-direction: column; position: relative;
                border: 4px solid ${color}; transform: scale(0.9); opacity: 0;
                animation: popIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
            ">
                <!-- Detective Image (Floating Left) -->
                <div style="position: absolute; bottom: 0; left: -100px; width: 200px; height: 200px; pointer-events: none;">
                    <img src="${imgPath}" alt="Detective" style="width: 100%; height: 100%; object-fit: contain; filter: drop-shadow(5px 5px 10px rgba(0,0,0,0.2));">
                </div>

                <!-- Content -->
                <div style="padding: 20px 20px 20px 110px; min-height: 120px; display: flex; flex-direction: column; justify-content: center;">
                    <h3 style="margin: 0 0 10px 0; color: ${color}; font-family: 'Baloo 2', cursive; font-size: 1.5rem;">
                        ${type === 'success' ? '¡Muy bien!' : 'Detective:'}
                    </h3>
                    <div style="font-size: 1.2rem; color: #333; font-family: 'Baloo 2', cursive; line-height: 1.4;">
                        ${message}
                    </div>
                </div>

                <!-- Footer / Button -->
                <div style="padding: 15px; border-top: 2px solid #F1F5F9; display: flex; justify-content: flex-end; background: #F8FAFC; border-radius: 0 0 16px 16px;">
                    <button id="fb-close-btn" style="
                        background: ${color}; color: white; border: none; padding: 10px 30px;
                        font-size: 1.2rem; border-radius: 12px; font-weight: bold; cursor: pointer;
                        box-shadow: 0 4px 0 rgba(0,0,0,0.1); transition: transform 0.1s;
                    ">
                        ENTENDIDO
                    </button>
                </div>
            </div>
            <style>
                @keyframes popIn { to { transform: scale(1); opacity: 1; } }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            </style>
        `;

        const container = document.getElementById(this.modalId);
        if (container) container.appendChild(modal); // Append to modal so it stays inside
        else document.body.appendChild(modal);

        // Events
        const closeBtn = modal.querySelector('#fb-close-btn');
        closeBtn.onclick = () => modal.remove();
        modal.onclick = (e) => {
            if (e.target === modal) modal.remove();
        };

        // TTS
        if (window.AudioService && window.AudioService.speak) {
            window.AudioService.speak(message);
        }
    },


    showConfirm(message, onConfirm) {
        // Remove existing feedback if any
        const existing = document.getElementById('workboard-feedback-modal');
        if (existing) existing.remove();

        // Determine Detective Character
        const char = (window.UserManager && typeof window.UserManager.loadData === 'function')
            ? (window.UserManager.loadData('mentorCharacter') || 'boy')
            : 'boy';

        const imgPath = `images/detectives/${char}/detective_${char}_neutral.png`;

        const modal = document.createElement('div');
        modal.id = 'workboard-feedback-modal';
        modal.style.cssText = `
            position: absolute; top: 0; left: 0; width: 100%; height: 100%;
            display: flex; align-items: center; justify-content: center;
            z-index: 10000; pointer-events: auto;
            background: rgba(0,0,0,0.5); backdrop-filter: blur(3px);
            animation: fadeIn 0.3s ease-out;
        `;

        modal.innerHTML = `
            <div style="
                background: white; width: 500px; max-width: 90%; border-radius: 20px;
                box-shadow: 0 20px 50px rgba(0,0,0,0.3); overflow: visible;
                display: flex; flex-direction: column; position: relative;
                border: 4px solid #3B82F6; transform: scale(0.9); opacity: 0;
                animation: popIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
            ">
                <!-- Detective Image (Floating Left) -->
                <div style="position: absolute; bottom: 0; left: -100px; width: 200px; height: 200px; pointer-events: none;">
                    <img src="${imgPath}" alt="Detective" style="width: 100%; height: 100%; object-fit: contain; filter: drop-shadow(5px 5px 10px rgba(0,0,0,0.2));">
                </div>

                <!-- Content -->
                <div style="padding: 20px 20px 20px 110px; min-height: 120px; display: flex; flex-direction: column; justify-content: center;">
                    <h3 style="margin: 0 0 10px 0; color: #3B82F6; font-family: 'Baloo 2', cursive; font-size: 1.5rem;">
                        Detective:
                    </h3>
                    <div style="font-size: 1.2rem; color: #333; font-family: 'Baloo 2', cursive; line-height: 1.4;">
                        ${message}
                    </div>
                </div>

                <!-- Footer / Buttons -->
                <div style="padding: 15px; border-top: 2px solid #F1F5F9; display: flex; justify-content: flex-end; gap: 10px; background: #F8FAFC; border-radius: 0 0 16px 16px;">
                    <button id="fb-cancel-btn" style="
                        background: #94A3B8; color: white; border: none; padding: 10px 20px;
                        font-size: 1.1rem; border-radius: 12px; font-weight: bold; cursor: pointer;
                        box-shadow: 0 4px 0 rgba(0,0,0,0.1);
                    ">
                        CANCELAR
                    </button>
                    <button id="fb-confirm-btn" style="
                        background: #3B82F6; color: white; border: none; padding: 10px 30px;
                        font-size: 1.1rem; border-radius: 12px; font-weight: bold; cursor: pointer;
                        box-shadow: 0 4px 0 rgba(0,0,0,0.1);
                    ">
                        ACEPTAR
                    </button>
                </div>
            </div>
            <style>
                @keyframes popIn { to { transform: scale(1); opacity: 1; } }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            </style>
        `;

        const container = document.getElementById(this.modalId);
        if (container) container.appendChild(modal);
        else document.body.appendChild(modal);

        // TTS of question
        if (window.AudioService && window.AudioService.speak) {
            window.AudioService.speak(message);
        }

        // Events
        const cancelBtn = modal.querySelector('#fb-cancel-btn');
        const confirmBtn = modal.querySelector('#fb-confirm-btn');

        cancelBtn.onclick = () => {
            this.playPop();
            modal.remove();
        };

        confirmBtn.onclick = () => {
            this.playPop();
            modal.remove();
            if (onConfirm) onConfirm();
        };
    },

    clearTable() {
        const zone = document.getElementById(this.workAreaId);
        if (!zone) return;

        this.showConfirm('¿Estás seguro de que quieres limpiar toda la mesa?', () => {
            const items = zone.querySelectorAll('.work-item');
            items.forEach(item => item.remove());
            this.playPop();
            document.querySelectorAll('.count-result-display').forEach(el => el.innerHTML = '');
            this.activeRegions.clear();
        });
    }
};

window.WorkBoard = WorkBoard;
document.addEventListener('DOMContentLoaded', () => window.WorkBoard.init());

